import type {
  SearchFilterConditionCustomAttributeField,
  SearchFilterOperator,
  SearchFilters,
  SearchFilterStrategy,
  SearchFilterValue,
  SearchScope,
} from 'lib-search-types';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import { Writable } from 'ts-essentials';

import {
  isCustomAttributeCondition,
  isLeafFilterCondition,
  isNullishFilterOperator,
} from './filterTypeGuards';

const DELIMITER = {
  FIELD: '~',
  VALUES: ',',
  CONDITIONS: ';',
  CUSTOM_ATTRIBUTE_FIELD: ':',
};

const customAttributeFieldRegex = new RegExp(
  `^(?:conversation|contact)_custom_attribute\\${DELIMITER.CUSTOM_ATTRIBUTE_FIELD}.*$`,
);
const customAttributeValuesRegex = /\(([^)]+)\)/g;

/**
 * Generates a URL-friendly string from a list of search filter conditions.
 *
 * @example
 *   strategy~all;status~is_one~open;tags~includes_all~1a2b3c,4d5e6f;
 *   conversation_custom_attribute:contact_reason~is_one~(payment,payment_refund),(other);
 *   contact_custom_attribute:external_id~is_one~12345,67890
 */
export function convertFiltersObjectToString<
  TScope extends SearchScope = SearchScope,
>(filters?: SearchFilters<TScope>): string {
  if (!filters?.filters.length) return '';

  try {
    const result = (filters.filters as any[])
      .filter(isLeafFilterCondition)
      .reduce<string[]>((acc, condition) => {
        const { operator, values } = condition;

        // Ignore conditions that are not IS_EMPTY or IS_NOT_EMPTY and that have no values
        if (!values.length && !isNullishFilterOperator(operator)) {
          return acc;
        }

        /**
         * Values are delimited by comma. If the value is an array of strings,
         * as it is the case for `string_list` custom attributes, it is also
         * wrapped in parentheses to denote it as a list of values.
         *
         * @example
         *   Input: [
         *     { value: ['payment', 'payment_refund'] },
         *     { value: ['other'] },
         *   ];
         *
         *   Output: '(payment, payment_refund),(other)';
         */
        const stringifiedValues = values.length
          ? values
              .map(({ value }) =>
                Array.isArray(value)
                  ? `(${value.join(DELIMITER.VALUES)})`
                  : value,
              )
              .join(DELIMITER.VALUES)
          : undefined;

        // If the condition is a custom attribute, the stringified field is in the form of
        // `conversation_custom_attribute:attributeId`.
        const field = isCustomAttributeCondition(condition)
          ? [condition.field, condition.attributeId].join(
              DELIMITER.CUSTOM_ATTRIBUTE_FIELD,
            )
          : condition.field;

        const string = [field, operator, stringifiedValues]
          .filter(Boolean)
          .join(DELIMITER.FIELD);

        acc.push(string);

        return acc;
      }, []);

    if (result.length) {
      result.unshift(`strategy${DELIMITER.FIELD}${filters.strategy}`);
    }
    return result.join(DELIMITER.CONDITIONS);
  } catch (error) {
    Sentry.captureMessage(`Search: Failed to stringify filters JSON`, {
      level: 'warning',
      tags: {
        team: SentryTeam.Insights,
      },
      extra: { filters },
    });

    return '';
  }
}

/**
 * Generates a list of search filter conditions from a URL-friendly string.
 *
 * @example
 *   strategy~all;status~is_one~open;tags~includes_all~1a2b3c,4d5e6f;
 *   conversation_custom_attribute:contact_reason~is_one~(payment,payment_refund),(other);
 *   contact_custom_attribute:external_id~is_one~12345,67890
 */
export function convertFiltersStringToObject<
  TScope extends SearchScope = SearchScope,
>(filters?: string): SearchFilters<TScope> {
  const initialOutput: Writable<SearchFilters> = {
    strategy: 'all',
    filters: [],
  };
  if (!filters) return initialOutput as SearchFilters<TScope>;

  try {
    const [strategyStr, ...conditions] = filters.split(DELIMITER.CONDITIONS);
    const strategy = strategyStr.split(
      DELIMITER.FIELD,
    )[1] as SearchFilterStrategy;

    if (['all', 'any'].includes(strategy)) {
      initialOutput.strategy = strategy as SearchFilterStrategy;
    }

    const parsedFilters = conditions.reduce((acc, conditionString) => {
      const [fieldStr, operator, valuesStr] = conditionString.split(
        DELIMITER.FIELD,
      ) as [string, SearchFilterOperator, string];

      // If the condition is a custom attribute, the stringified field is in the form of
      // `conversation_custom_attribute:attributeId`.
      if (customAttributeFieldRegex.test(fieldStr)) {
        const [field, attributeId] = fieldStr.split(
          DELIMITER.CUSTOM_ATTRIBUTE_FIELD,
        ) as [SearchFilterConditionCustomAttributeField, string];

        const values: SearchFilterValue[] = [];

        if (valuesStr) {
          const isStringListValue = customAttributeValuesRegex.test(valuesStr);

          // In the case of `string_list` custom attributes, the comma-separated values are also
          // wrapped in parentheses, to denote them as lists of values.
          if (isStringListValue) {
            const matches = valuesStr.match(customAttributeValuesRegex);

            if (matches?.length) {
              values.push(
                ...matches.map((match) => ({
                  // For `string_list` custom attributes, the label is resolved from the custom
                  // attributes API data in the appropriate component.
                  label: '',
                  value: match.slice(1, -1).split(DELIMITER.VALUES),
                })),
              );
            }
          } else {
            values.push(
              ...valuesStr.split(DELIMITER.VALUES).map((value) => ({
                label: value,
                value,
              })),
            );
          }
        }

        (acc.filters as any[]).push({
          field,
          operator,
          values,
          attributeId,
        });
      } else {
        const values = valuesStr
          ? valuesStr
              .split(DELIMITER.VALUES)
              .map((value) => ({ label: '', value }))
          : [];

        (acc.filters as any[]).push({
          field: fieldStr,
          operator,
          values,
        });
      }

      return acc;
    }, initialOutput);

    return parsedFilters as unknown as SearchFilters<TScope>;
  } catch (error) {
    Sentry.captureMessage(`Search: Failed to parse filters string`, {
      level: 'warning',
      tags: {
        team: SentryTeam.Insights,
      },
      extra: { filters },
    });

    return initialOutput as SearchFilters<TScope>;
  }
}
